import { useState } from 'react';
import { Binary, Copy, Check, ArrowLeftRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const Base64Tool = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [copied, setCopied] = useState(false);

  const encode = () => {
    try {
      const encoded = btoa(unescape(encodeURIComponent(input)));
      setOutput(encoded);
      toast.success('Text encoded to Base64!');
    } catch (err) {
      toast.error('Failed to encode text');
    }
  };

  const decode = () => {
    try {
      const decoded = decodeURIComponent(escape(atob(input)));
      setOutput(decoded);
      toast.success('Base64 decoded!');
    } catch (err) {
      toast.error('Invalid Base64 string');
    }
  };

  const handleAction = () => {
    if (mode === 'encode') {
      encode();
    } else {
      decode();
    }
  };

  const copyOutput = () => {
    if (output) {
      navigator.clipboard.writeText(output);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const swapMode = () => {
    setMode(mode === 'encode' ? 'decode' : 'encode');
    setInput(output);
    setOutput('');
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <Binary className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Base64 Encoder/Decoder</h1>
          <p className="text-muted-foreground">
            Encode text to Base64 or decode Base64 to text
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Input */}
        <Card className="glass border-border/50">
          <CardHeader>
            <CardTitle>
              {mode === 'encode' ? 'Text Input' : 'Base64 Input'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder={mode === 'encode' 
                ? 'Enter text to encode...' 
                : 'Enter Base64 string to decode...'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="min-h-[250px] font-mono text-sm resize-none"
            />
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleAction} disabled={!input.trim()}>
                {mode === 'encode' ? 'Encode' : 'Decode'}
              </Button>
              <Button variant="outline" onClick={swapMode} className="gap-2">
                <ArrowLeftRight className="h-4 w-4" />
                Switch to {mode === 'encode' ? 'Decode' : 'Encode'}
              </Button>
              <Button variant="ghost" onClick={clearAll}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Output */}
        <Card className="glass border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{mode === 'encode' ? 'Base64 Output' : 'Decoded Text'}</span>
              {output && (
                <Button variant="ghost" size="sm" onClick={copyOutput} className="gap-2">
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  Copy
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Result will appear here..."
              value={output}
              readOnly
              className="min-h-[250px] font-mono text-sm resize-none bg-muted/30"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Base64Tool;